// src/components/BioGeneratorModal.js
import React, { useState } from 'react';
import { X, Sparkles, ChevronLeft, Loader2 } from 'lucide-react';
import { generateAiContent } from '../services/ai-content-service';

const BioGeneratorModal = ({ isOpen, onClose, onBioGenerated }) => {
  const [step, setStep] = useState(1); // 1 = questions, 2 = style, 3 = generated
  const [answers, setAnswers] = useState({
    services: '',
    journey: '',
    strengths: '',
    purpose: ''
  });
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [generatedBio, setGeneratedBio] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const styles = [
    {
      id: 'professional',
      name: 'Professional & Credible',
      description: 'Experienced practitioner focused on results and transformation',
      example: 'With over 8 years of experience...'
    },
    {
      id: 'warm',
      name: 'Warm & Approachable',
      description: 'Friendly guide who creates a safe space for your journey',
      example: 'I believe in creating a welcoming space...'
    },
    {
      id: 'mystical',
      name: 'Mystical & Spiritual',
      description: 'Intuitive healer connected to ancient wisdom and energy',
      example: 'Guided by intuition and cosmic wisdom...'
    }
  ];

  const questions = [
    {
      id: 'services',
      label: 'What spiritual services do you offer or plan to offer?',
      placeholder: 'Example: "I offer tarot readings, energy healing, and spiritual coaching"',
      helper: 'Just write naturally - 1-3 sentences is perfect'
    },
    {
      id: 'journey',
      label: 'Tell us about your journey with these practices. Are you just starting out, or have you been doing this for a while?',
      placeholder: 'Example: "I\'ve been reading tarot for 5 years, starting with friends and family before going professional" or "I\'m just beginning, but I\'ve always felt a deep calling to help others through spiritual guidance"',
      helper: 'Whether you\'re brand new or have decades of experience, every practitioner\'s journey is unique. Share yours!'
    },
    {
      id: 'strengths',
      label: 'What are you naturally good at when working with clients?',
      placeholder: 'Example: "I\'m great at helping people feel comfortable opening up" or "I have a gift for seeing patterns and connections that others miss"',
      helper: 'Maybe you create a safe, welcoming space. Maybe you have strong intuition. Maybe you explain things clearly. What comes naturally to you?'
    },
    {
      id: 'purpose',
      label: 'Why do you do this work? What do you hope to bring to the people you work with?',
      placeholder: 'Example: "I want to help people find clarity during difficult transitions" or "This work lets me use my gifts to bring healing and peace to others"',
      helper: 'What lights you up about this? What difference do you hope to make?'
    }
  ];

  const handleAnswerChange = (questionId, value) => {
    setAnswers({
      ...answers,
      [questionId]: value
    });
  };

  const canProceedToStyles = () => {
    // Check if at least first 2 questions are answered
    return answers.services.trim().length > 10 && answers.journey.trim().length > 10;
  };

  // Helper function for better mock generation
  const generateMockBio = (answers, style) => {
    // Extract key info without repetition
    const services = answers.services.replace(/^(I offer |I provide |I do )/i, '').trim();
    const experience = answers.journey.split('.')[0].replace(/^(I'?m |I have |I've )/i, '').trim();
    const strength = answers.strengths.split('.')[0].replace(/^(I'?m |I have |I am )/i, '').trim();
    const purpose = answers.purpose.split('.')[0].replace(/^(I want to |I hope to |I help )/i, '').trim();

    if (style === 'professional') {
      return `As a practitioner specializing in ${services}, I bring ${experience.toLowerCase()} to every session. My approach is grounded in ${strength.toLowerCase()}, ensuring clients receive practical guidance alongside spiritual insight. I'm dedicated to ${purpose.toLowerCase()}, providing a structured yet intuitive framework for personal transformation. Each session is tailored to your unique needs, combining proven techniques with deep intuitive understanding.`;
    } else if (style === 'warm') {
      return `I offer ${services} in a warm, welcoming environment where you can truly be yourself. ${experience}. I'm naturally ${strength.toLowerCase()}, which helps create a safe space for deep exploration. My passion is ${purpose.toLowerCase()}, and I believe everyone deserves compassionate support on their spiritual journey. When we work together, you'll find both practical guidance and genuine care every step of the way.`;
    } else { // mystical
      return `Through ${services}, I serve as a guide for those seeking deeper spiritual connection. ${experience}. My gift lies in ${strength.toLowerCase()}, allowing me to perceive the energies and patterns that shape your path. I'm called to ${purpose.toLowerCase()}, working with divine guidance and intuitive wisdom. In our sessions together, we'll explore the mystical dimensions of your journey, connecting with your higher self and the universal energies that surround you.`;
    }
  };

  const handleGenerateBio = async (retryCount = 0) => {
    setIsGenerating(true);

    try {
      let bio = (await generateAiContent('bio', answers, selectedStyle)).trim();

      if (!bio) {
        throw new Error('Empty response from AI service');
      }

      // Validate output
      const wordCount = bio.split(' ').filter(w => w).length;

      if (wordCount < 75 || wordCount > 125) {
        console.warn(`Bio word count out of range: ${wordCount} words`);
        // Retry once if too long/short
        throw new Error('Word count validation failed');
      }

      // Check for common issues
      if (bio.toLowerCase().includes('i offer i offer')) {
        throw new Error('Repetition detected');
      }

      if (!bio.endsWith('.') && !bio.endsWith('!') && !bio.endsWith('?')) {
        bio += '.'; // Add period if missing
      }

      setGeneratedBio(bio);
      setStep(3);

    } catch (error) {
      console.error('Error generating bio:', error);

      // Retry once automatically
      if (retryCount === 0) {
        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        return handleGenerateBio(1); // Retry with count
      }

      // After retry fails, use improved mock
      const mockBio = generateMockBio(answers, selectedStyle);
      setGeneratedBio(mockBio);
      setStep(3);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    setIsGenerating(true);
    await handleGenerateBio();
  };

  const handleTryAnotherStyle = () => {
    setSelectedStyle(null);
    setStep(2);
  };

  const handleUseBio = () => {
    onBioGenerated(generatedBio);
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setAnswers({ services: '', journey: '', strengths: '', purpose: '' });
    setSelectedStyle(null);
    setGeneratedBio('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600" />
              </button>
            )}
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {step === 1 ? "Let's write your bio!" : step === 2 ? 'Choose your style' : 'Your bio is ready'}
              </h2>
              <p className="text-sm text-gray-600">
                Step {step} of 3
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Questions */}
          {step === 1 && (
            <div className="space-y-6">
              <p className="text-gray-700">
                Tell us about yourself so we can write your bio:
              </p>

              {questions.map((question, index) => (
                <div key={question.id}>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    {index + 1}. {question.label}
                  </label>
                  <textarea
                    value={answers[question.id]}
                    onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    placeholder={question.placeholder}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                  <p className="text-xs text-gray-500 mt-1">{question.helper}</p>
                </div>
              ))}

              <button
                onClick={() => setStep(2)}
                disabled={!canProceedToStyles()}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Continue to Choose Style
              </button>

              {!canProceedToStyles() && (
                <p className="text-sm text-amber-600 text-center">
                  Please answer at least the first two questions to continue
                </p>
              )}
            </div>
          )}

          {/* Step 2: Style Selection */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-gray-700 mb-4">
                Choose the tone that best represents you:
              </p>

              {styles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`w-full text-left p-4 border-2 rounded-xl transition-all ${
                    selectedStyle === style.id
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                      selectedStyle === style.id
                        ? 'border-purple-600 bg-purple-600'
                        : 'border-gray-300'
                    }`}>
                      {selectedStyle === style.id && (
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 mb-1">{style.name}</p>
                      <p className="text-sm text-gray-600 mb-2">{style.description}</p>
                      <p className="text-sm text-gray-500 italic">"{style.example}"</p>
                    </div>
                  </div>
                </button>
              ))}

              <button
                onClick={handleGenerateBio}
                disabled={!selectedStyle || isGenerating}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Generating your bio...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5" />
                    Generate Bio
                  </>
                )}
              </button>
            </div>
          )}

          {/* Step 3: Generated Bio */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-purple-900">
                    {styles.find(s => s.id === selectedStyle)?.name} style
                  </span>
                  <span className="text-sm text-purple-700">
                    {generatedBio.split(' ').filter(w => w).length} words
                  </span>
                </div>
                <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">
                  {generatedBio}
                </p>
              </div>

              {generatedBio.split(' ').filter(w => w).length < 75 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-800">
                    ⚠️ This bio is shorter than recommended. Consider regenerating.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleTryAnotherStyle}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Try Another Style
                </button>
                <button
                  onClick={handleRegenerate}
                  disabled={isGenerating}
                  className="px-4 py-2 border border-purple-600 text-purple-600 rounded-lg font-medium hover:bg-purple-50 transition-colors disabled:opacity-50"
                >
                  {isGenerating ? 'Generating...' : 'Regenerate'}
                </button>
              </div>

              <button
                onClick={handleUseBio}
                className="w-full px-6 py-3 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition-colors"
              >
                Use This Bio
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BioGeneratorModal;
